import { IBranchDto } from '../department/dtos'
import { mapBranchFromDto, mapBranchToDto } from '../department/helpers'
import { IS_ESOFT_USER_KEY } from '../http'
import { IContactPersonDto, IUserBranchDto, IUserDto, IWatcherDto } from './dtos'
import { IContactPerson, IUserBranchAccess, UserAccess, IUser, IWatcher } from './models'

export function getInitialsFromFullName(fullName: string) {
	return fullName
		.split(/\s/)
		.reduce((acc, word) => (acc += word.slice(0, 1)), '')
		.slice(0, 2)
		.toUpperCase()
}

export function getInitials(firstName: string, lastName: string) {
	return `${firstName} ${lastName}`
		.split(/\s/)
		.reduce((acc, word) => (acc += word.slice(0, 1)), '')
		.slice(0, 2)
		.toUpperCase()
}

export function isInternalUser(email: string) {
	return !!email?.match(/@esoft.com/)
}

export function isEsoftUser() {
	return typeof window !== 'undefined' ? localStorage.getItem(IS_ESOFT_USER_KEY) === 'true' : false
}

export function mapWatcherFromDto({
	axContactPersonId,
	userId,
	userEmail,
	userFirstName,
	userLastName,
}: IWatcherDto): IWatcher {
	return {
		id: userId,
		contactPersonId: axContactPersonId,
		email: userEmail,
		firstName: userFirstName,
		lastName: userLastName,
	}
}

export function mapUserFromDto(userDto: IUserDto, authUserBranches?: IBranchDto[] | number): IUser {
	const {
		id,
		email,
		firstName,
		lastName,
		fullName,
		phone,
		isFromEsoft,
		settings,
		currentlyImpersonatingEmail,
		currentlyImpersonatingName,
		currentlyImpersonatingUserId,
		contactPersonId,
	} = userDto

	const branches = Array.isArray(authUserBranches) ? authUserBranches : userDto.branches
	const currentFirstName = currentlyImpersonatingName ? currentlyImpersonatingName.split(' ')?.[0] : firstName
	const currentLastName = currentlyImpersonatingName
		? currentlyImpersonatingName.substring(currentFirstName.length)
		: lastName
	const currentFullName = currentlyImpersonatingName || fullName || `${firstName} ${lastName}`

	return {
		id: currentlyImpersonatingUserId || id,
		firstName: currentFirstName,
		lastName: currentLastName,
		phoneNumber: phone,
		fullName: currentFullName,
		initials: getInitialsFromFullName(currentFullName),
		email: currentlyImpersonatingEmail || email,
		isInternalUser: !isFromEsoft,
		visiblePrices: !!settings?.showPrices,
		branches: branches.map(mapBranchFromDto),
		contactPersonId,
		_dto: userDto,
	}
}

export function mapUserToDto({
	id,
	firstName,
	lastName,
	email,
	phoneNumber,
	branches,
	visiblePrices,
	_dto,
}: IUser): IUserDto {
	return {
		..._dto,
		id,
		firstName,
		lastName,
		email,
		phone: phoneNumber,
		settings: {
			..._dto?.settings,
			showPrices: visiblePrices,
		},
		branches: branches.filter(department => department.access !== UserAccess.None).map(mapBranchToDto),
	}
}

export function mapUserBranchFromDto(
	userBranchDto: IUserBranchDto,
	branchId: IBranchDto['axCustomerId']
): IUserBranchAccess {
	const { id, firstName, lastName, isAdmin, email, phone, contactPersonId } = userBranchDto

	return {
		id,
		fullName: `${firstName} ${lastName ?? ''}`,
		branchId,
		email,
		phoneNumber: phone,
		currentBranchContactPersonId: contactPersonId,
		access: isAdmin ? UserAccess.Admin : UserAccess.Regular,
		_dto: userBranchDto,
	}
}

export function mapUserBranchToDto({
	id,
	fullName,
	email,
	phoneNumber,
	access,
	_dto,
}: IUserBranchAccess): IUserBranchDto {
	const firstName = fullName.split(' ')[0]
	const lastName = fullName.substring(firstName.length)

	return {
		..._dto,
		id,
		firstName,
		lastName,
		isAdmin: access === UserAccess.Admin,
		email,
		phone: phoneNumber,
	}
}

export function mapContactPersonFromDto({
	name,
	contactType,
	contactInfo,
}: IContactPersonDto): IContactPerson | undefined {
	if (contactType === 'NONE') {
		return undefined
	}

	return {
		type: contactType,
		name,
		email: contactInfo?.email,
		phone: contactInfo?.phoneMobile,
		phoneAlt: contactInfo?.phonePrivate || contactInfo?.phoneWork,
	}
}

export function mapUserAccessToUser({ id, fullName, email, phoneNumber }: IUserBranchAccess): Partial<IUser> {
	const firstName = fullName.split(' ')[0]
	const lastName = fullName.substring(firstName.length)

	return {
		id,
		firstName,
		lastName,
		email,
		phoneNumber,
	}
}
